import { api } from './client.js';

export interface Food {
  foodId: string;
  name: string;
  nameKey: string;
  kcalPer100g: number | null;
  kcalPerPortion: number | null;
  proteinPer100g: number | null;
  proteinPerPortion: number | null;
  portionLabel: string | null;
  usageCount: number;
  lastUsedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface FoodLog {
  logId: string;
  date: string;
  label: string;
  kcal: number;
  protein: number | null;
  grams: number | null;
  foodId: string | null;
  createdAt: string;
}

export interface NutritionGoal {
  validFrom: string;
  kcalTarget: number;
  proteinTarget: number | null;
  createdAt: string;
}

export interface FoodInput {
  name: string;
  kcalPer100g?: number | null;
  kcalPerPortion?: number | null;
  proteinPer100g?: number | null;
  proteinPerPortion?: number | null;
  portionLabel?: string | null;
}

export interface FoodLogInput {
  label?: string;
  foodId?: string;
  grams?: number;
  kcal?: number;
  protein?: number;
  portionLabel?: string;
  date?: string;
  remember?: boolean;
  kcalOverride?: boolean;
}

export interface DaySummary {
  date: string;
  kcal: number;
  protein: number;
  kcalTarget: number | null;
  kcalRemaining: number | null;
  kcalPct: number | null;
  proteinTarget: number | null;
  proteinRemaining: number | null;
}

export interface WeekDayPoint {
  date: string;
  kcal: number;
  target: number | null;
}

export interface WeekSummary {
  monday: string;
  budget: number | null;
  spent: number;
  remaining: number | null;
  days: WeekDayPoint[];
}

export interface Summary {
  day: DaySummary;
  week: WeekSummary;
}

export interface DayEntries {
  date: string;
  entries: FoodLog[];
  totals: { kcal: number; protein: number };
}

export interface LogResult {
  log: FoodLog;
  food: Food | null;
  possibleDuplicate: boolean;
}

// --- Catalogue ---

export async function listFoods(): Promise<Food[]> {
  const r = await api<{ items: Food[] }>('/food');
  return r.items;
}

export async function createFood(input: FoodInput): Promise<Food> {
  const r = await api<{ item: Food }>('/food', {
    method: 'POST',
    body: JSON.stringify(input),
  });
  return r.item;
}

export async function updateFood(foodId: string, patch: Partial<FoodInput>): Promise<Food> {
  const r = await api<{ item: Food }>(`/food/${foodId}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
  return r.item;
}

export async function deleteFood(foodId: string): Promise<void> {
  await api<void>(`/food/${foodId}`, { method: 'DELETE' });
}

// --- Logs ---

export async function listDay(date?: string): Promise<DayEntries> {
  const qs = date ? `?date=${date}` : '';
  return api<DayEntries>(`/food/log${qs}`);
}

export async function createLog(input: FoodLogInput): Promise<LogResult> {
  return api<LogResult>('/food/log', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

// The date is part of the sort key, hence in the path rather than the body.
export async function updateLog(
  date: string,
  logId: string,
  patch: { label?: string; kcal?: number; protein?: number | null; grams?: number | null }
): Promise<FoodLog> {
  const r = await api<{ log: FoodLog }>(`/food/log/${date}/${logId}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
  return r.log;
}

export async function deleteLog(date: string, logId: string): Promise<void> {
  await api<void>(`/food/log/${date}/${logId}`, { method: 'DELETE' });
}

// --- Summary & goal ---

export async function getSummary(date?: string): Promise<Summary> {
  const qs = date ? `?date=${date}` : '';
  return api<Summary>(`/food/summary${qs}`);
}

export async function getGoal(): Promise<NutritionGoal | null> {
  const r = await api<{ goal: NutritionGoal | null }>('/nutrition/goal');
  return r.goal;
}

export async function saveGoal(input: {
  kcalTarget: number;
  proteinTarget?: number | null;
  validFrom?: string;
}): Promise<NutritionGoal> {
  const r = await api<{ goal: NutritionGoal }>('/nutrition/goal', {
    method: 'POST',
    body: JSON.stringify(input),
  });
  return r.goal;
}
